import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { opsDevicesService } from '../../../services/OpsDevicesService';

interface DecommissionModalProps {
  open: boolean;
  serialNumber: string;
  tenantName?: string | null;
  activatedAt?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DecommissionModal({
  open,
  serialNumber,
  tenantName,
  activatedAt,
  onClose,
  onSuccess,
}: DecommissionModalProps) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm the action by checking the box.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await opsDevicesService.decommissionDevice(serialNumber, reason.trim());
      onSuccess();
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Decommission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Decommission Device
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Serial:</strong> {serialNumber}
          </Typography>
          {tenantName && (
            <Typography variant="body2" gutterBottom>
              <strong>Tenant:</strong> {tenantName}
            </Typography>
          )}
          {activatedAt && (
            <Typography variant="body2" gutterBottom>
              <strong>Activated:</strong> {new Date(activatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          After decommission, to provision this PC again:
          <Box component="ol" sx={{ marginTop: '4px', paddingLeft: '20px', margin: '4px 0 0 0', pl: '20px' }}>
            <li>Open device detail and click <strong>Unlock Reprovisioning</strong></li>
            <li>Click <strong>Trigger Reprovisioning</strong> (or reboot the PC after unlock)</li>
          </Box>
        </Alert>

        <Alert severity="warning" sx={{ mb: 2 }}>
          This will:
          <Box component="ul" sx={{ marginTop: '4px', paddingLeft: '20px', margin: '4px 0 0 0', pl: '20px' }}>
            <li>Revoke and permanently delete the device's AWS IoT certificate</li>
            <li>Immediately disconnect the Edge PC from the cloud</li>
            <li>Send an SSM command to remove local provision state under <code>C:\Cnapse\edge-provisioner-state</code> when the PC is registered in SSM</li>
            <li>Retain the SSM instance ID in inventory for remote recovery (reboot, reprovision trigger)</li>
          </Box>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AppTextField
          label="Reason *"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Describe why this device is being decommissioned"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I understand this action cannot be reversed remotely."
        />
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting || !confirmed || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Decommission
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
